/**
 * IAPService — In-App Purchase via RevenueCat
 * ─────────────────────────────────────────────
 * Flux: user achète → RevenueCat gère la transaction Google Play →
 *        RevenueCat vérifiez le receipt → webhook côté backend active le tier en DB
 *        → le client recharge son profil et voit son nouveau tier
 *
 * POURQUOI RevenueCat (vs expo-iap ou Billing direct) :
 *  - Vérification des receipts automatique côté RevenueCat (pas besoin
 *    de coder /verify-purchase dans le Lambda)
 *  - Dashboard pour suivre MRR, renouvellements, churn
 *  - Webhooks qui notifient notre backend des events (purchase, renew,
 *    cancel, refund) → source de vérité pour notre DB
 *  - Multi-stores : bascule vers iOS sans toucher au code produit
 *  - Gratuit jusqu'à 2 500 $/mois de MRR
 *
 * SETUP REQUIS :
 *  1. Créer un compte sur https://www.revenuecat.com (gratuit)
 *  2. Dans le dashboard RevenueCat:
 *     - Créer une "app" (Android)
 *     - Ajouter la clé API "Google Service Account" depuis Play Console
 *     - Créer les Products (basic_monthly, basic_yearly, ...) liés aux
 *       productIds Play Store
 *     - Créer les Entitlements (basic, premium, gold) qui regroupent
 *       monthly+yearly du même tier
 *     - Copier la "Android API Key" (ligne ci-dessous REVENUECAT_ANDROID_KEY)
 *  3. Dans Google Play Console:
 *     - Créer les 6 produits d'abonnement (voir GUIDE_DEPLOIEMENT.md)
 *     - Partager la clé Service Account avec RevenueCat
 *  4. Configurer le webhook RevenueCat → https://xxx.execute-api/prod/revenuecat-webhook
 *     avec l'en-tête 'Authorization: Bearer <REVENUECAT_WEBHOOK_SECRET>'
 */
import { Platform, DeviceEventEmitter } from 'react-native';
import { activateSubscription } from './SubscriptionService';

// ═══ CLÉS RevenueCat — à renseigner après création du compte RC ═══
// ⚠️ Ces clés sont "public keys" côté client, OK d'être dans le bundle.
// Les vraies clés privées (Google Service Account JSON) restent côté RevenueCat.
const REVENUECAT_ANDROID_KEY = 'goog_IVFPCFIkNFhqtaOVbamOVSdYvBS'; // RevenueCat Public Android SDK Key
const REVENUECAT_IOS_KEY     = 'appl_ULqUKliWkIRIRfPKdUTpxRUtBKQ'; // RevenueCat Public iOS SDK Key (v1.7.89)

// ═══ PRODUCT IDS — doivent correspondre exactement à Play Console ═══
// On supporte les 3 tiers payants × 2 périodes = 6 produits
// IMPORTANT : ces IDs sont aussi utilisés côté RevenueCat pour mapper
// vers les Entitlements (basic, premium, gold).
export const PRODUCT_IDS = {
  basic_monthly:   'outfit_basic_monthly',
  basic_yearly:    'outfit_basic_yearly',
  premium_monthly: 'outfit_premium_monthly',
  premium_yearly:  'outfit_premium_yearly',
  gold_monthly:    'outfit_gold_monthly',
  gold_yearly:     'outfit_gold_yearly',
};

// ═══ ENTITLEMENTS RevenueCat ═══
// Un "Entitlement" regroupe plusieurs produits qui débloquent le même niveau.
// basic_monthly + basic_yearly → Entitlement "basic_access"
// premium_monthly + premium_yearly → Entitlement "premium_access"
// gold_monthly + gold_yearly → Entitlement "gold_access"
// Quand un user a l'Entitlement "premium_access" actif, il a accès Premium.
//
// IMPORTANT : on garde les "tiers" courts (basic/premium/gold) comme source
// de vérité applicative, mais on mappe vers les identifiers RC qui ont
// le suffixe "_access" (convention choisie côté dashboard RC).
const ENTITLEMENT_TIERS = ['basic', 'premium', 'gold'];
const TIER_TO_ENTITLEMENT = {
  basic:   'basic_access',
  premium: 'premium_access',
  gold:    'gold_access',
};
// Mapping inverse pour lire les entitlements actifs venant de RC
const ENTITLEMENT_TO_TIER = {
  basic_access:   'basic',
  premium_access: 'premium',
  gold_access:    'gold',
};

// ═══ Conversion productId → { tier, period } ═══
export function parseProductId(productId) {
  if (!productId) return null;
  const lower = productId.toLowerCase();
  let tier = null;
  let period = null;
  for (const t of ENTITLEMENT_TIERS) {
    if (lower.includes(t)) { tier = t; break; }
  }
  if (lower.includes('yearly') || lower.includes('annual')) period = 'yearly';
  else if (lower.includes('monthly') || lower.includes('month')) period = 'monthly';
  if (!tier || !period) return null;
  return { tier, period };
}

// ─── Chargement du module (lazy) ─────────────────────────────
// react-native-purchases est dans les deps mais le module natif
// n'est disponible qu'après un `npx expo prebuild` + build EAS.
// En Expo Go il échoue silencieusement (mode dev).
let Purchases = null;
let purchasesInitialized = false;

async function getPurchases() {
  if (Purchases) return Purchases;
  try {
    const mod = await import('react-native-purchases');
    Purchases = mod.default || mod;
    return Purchases;
  } catch (e) {
    console.warn('[IAP] react-native-purchases unavailable (Expo Go? prebuild needed?)');
    return null;
  }
}

// ─── Initialiser SDK RevenueCat ──────────────────────────────
// À appeler au démarrage de l'app, avec l'ID Cognito du user pour le
// lier à son profil RevenueCat (évite les abonnements orphelins).
//
// Sur web : RevenueCat exige une clé "Web Billing" séparée des clés
// iOS/Android. Comme on utilise Stripe pour les paiements web (cf.
// StripeService.js), on skip l'init RC sur web pour éviter des warnings
// "Invalid API key" inutiles dans la console.
export async function initIAP(userId = null) {
  if (Platform.OS === 'web') return false;
  const RC = await getPurchases();
  if (!RC) return false;
  // ═══ Idempotence stricte (v1.7.59) ═══
  // Si déjà initialisé avec succès, on ne re-configure pas (et on évite
  // l'erreur "singleton instance already configured" sur certaines versions
  // du SDK). Pour changer d'user → utiliser logIn() ou logOut().
  if (purchasesInitialized) return true;
  try {
    const apiKey = Platform.OS === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
    if (!apiKey || apiKey.includes('XXXXXXXX')) {
      const which = Platform.OS === 'ios' ? 'REVENUECAT_IOS_KEY' : 'REVENUECAT_ANDROID_KEY';
      console.warn(`[IAP] ❌ ${which} manquante ou placeholder. Remplacer dans src/services/IAPService.js (ligne 39-40).`);
      console.warn(`[IAP] Récupérer la clé sur https://app.revenuecat.com → Project Settings → API Keys`);
      return false;
    }

    // Config : on passe le userId Cognito comme appUserID pour que RevenueCat
    // associe les achats au bon user, même si le user change de device.
    await RC.configure({ apiKey, appUserID: userId || undefined });

    // ═══ v1.7.59 : valider que le singleton est bien instancié ═══
    // Avant : on settait purchasesInitialized=true juste après configure(),
    //   mais le natif RC peut ne pas avoir fini son init quand un autre
    //   thread (useEffect AuthContext) appelle setEmail/getOfferings →
    //   "no singleton instance". On vérifiez que getCustomerInfo() marche.
    try {
      await RC.getCustomerInfo(); // throw si singleton pas ready
      purchasesInitialized = true;
      console.log('[IAP] ✓ RevenueCat initialized for user:', userId || 'anonymous');
      return true;
    } catch (validationErr) {
      // Tentative de récupération : attendre 200ms et retry une fois
      await new Promise(r => setTimeout(r, 200));
      try {
        await RC.getCustomerInfo();
        purchasesInitialized = true;
        console.log('[IAP] ✓ RevenueCat initialized (after retry) for user:', userId || 'anonymous');
        return true;
      } catch (retryErr) {
        console.warn('[IAP] configure() OK but singleton not ready:', retryErr.message);
        return false;
      }
    }
  } catch (e) {
    console.warn('[IAP] init error:', e.message);
    return false;
  }
}

// ─── Re-lier après login ─────────────────────────────────────
// Appelé quand l'user se connecte pour associer ses achats à son compte.
//
// ═══ FIX 2026-04 (v1.7.53) : pousser l'email + attributes vers RC ═══
// ═══ FIX 2026-04 (v1.7.59) : auto-configure si pas encore initialisé ═══
// Avant v1.7.59 : si identifyUser() était appelé avant initIAP() (typiquement
//   par AuthContext useEffect au login), le SDK RC n'avait pas son
//   Purchases.configure() encore exécuté → erreur "no singleton instance,
//   make sure you configure purchases before trying to get the default
//   instance". Ça plantait silencieusement le setEmail() et bloquait les
//   achats ultérieurs.
// Maintenant : on appelle initIAP(userId) en interne si purchasesInitialized
//   est false. Idempotent : safe à appeler plusieurs fois.
export async function identifyUser(userId, options = {}) {
  // Web : Stripe assure le lien user-paiement via metadata cognito_sub,
  // pas besoin de RC.
  if (Platform.OS === 'web') return;
  const RC = await getPurchases();
  if (!RC || !userId) return;

  // ═══ Auto-initialisation si pas encore fait ═══
  if (!purchasesInitialized) {
    const ok = await initIAP(userId);
    if (!ok) {
      console.warn('[IAP] identifyUser: initIAP failed, skipping RC identification');
      return;
    }
  }

  try {
    const result = await RC.logIn(userId);
    const created = result?.created;
    console.log(`[IAP] logged in to RevenueCat as: ${userId.slice(0,12)} (created=${created})`);

    // ═══ Set des subscriber attributes pour visibilité dashboard RC ═══
    const { email, displayName, country, locale } = options;
    try {
      if (email && typeof RC.setEmail === 'function') {
        await RC.setEmail(email);
        console.log(`[IAP] ✓ RC setEmail: ${email}`);
      }
      if (displayName && typeof RC.setDisplayName === 'function') {
        await RC.setDisplayName(displayName);
      }
      // Attributs custom pour debug/segmentation RC
      const customAttrs = {};
      if (country) customAttrs.country = country;
      if (locale)  customAttrs.locale  = locale;
      // Marquer l'environnement (sandbox détectable côté serveur webhook RC)
      customAttrs.app_version = require('../../app.json').expo?.version || 'unknown';
      customAttrs.identified_at = new Date().toISOString();

      if (Object.keys(customAttrs).length > 0 && typeof RC.setAttributes === 'function') {
        await RC.setAttributes(customAttrs);
        console.log(`[IAP] ✓ RC setAttributes:`, Object.keys(customAttrs).join(','));
      }
    } catch (attrErr) {
      // Non-bloquant : si l'API a changé, on garde le logIn quand même
      console.warn('[IAP] setEmail/setAttributes failed (non-bloquant):', attrErr.message);
    }
  } catch (e) {
    console.warn('[IAP] logIn error:', e.message);
  }
}

// ─── Logout RC (rebascule en anonymous) ──────────────────────
// Appelé au signOut pour qu'au prochain login un nouvel identifyUser
// soit fait proprement (sinon RC garde le user précédent → confusion).
export async function logOutPurchases() {
  const RC = await getPurchases();
  if (!RC) return;
  try {
    await RC.logOut();
    console.log('[IAP] logged out from RevenueCat (back to anonymous)');
  } catch (e) {
    // logOut throw si déjà anonymous → safe à ignorer
    console.log('[IAP] logOut: already anonymous or no session');
  }
}

// ─── Charger les offres disponibles depuis RevenueCat ────────
// RevenueCat regroupe les produits en "Offerings" (ex: "default").
// Chaque Offering contient les Packages (monthly, annual, ...).
export async function loadOfferings() {
  const RC = await getPurchases();
  if (!RC) return null;
  try {
    if (!purchasesInitialized) await initIAP();
    const offerings = await RC.getOfferings();
    const current = offerings?.current;
    if (!current) return null;
    return {
      identifier: current.identifier,
      availablePackages: current.availablePackages || [],
    };
  } catch (e) {
    console.warn('[IAP] loadOfferings error:', e.message);
    return null;
  }
}

// ─── Acheter un abonnement ───────────────────────────────────
// tier: 'basic' | 'premium' | 'gold'
// period: 'monthly' | 'yearly'
// user: objet { id, email, name, pushToken, ... }
export async function purchaseSubscription(tier, period, user) {
  // v1.12.369 — GA4 event: checkout_started (mobile IAP via RevenueCat)
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('checkout_started', {
      tier,
      period,
      gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play',
      platform: Platform.OS,
    });
  } catch {}
  const RC = await getPurchases();

  // Fallback DEV : si RevenueCat absent (Expo Go), activation manuelle
  // (pour tester le flow Premium côté app sans vrai paiement).
  if (!RC) {
    console.warn('[IAP] react-native-purchases unavailable — dev manual activation');
    return await activateSubscription({
      tier,
      period,
      platform: 'manual',
      billingEmail: user?.email,
      billingName:  user?.name,
    });
  }

  // ═══ v1.7.66 : guard explicite si initIAP échoue ═══
  // Avant : on faisait `if (!purchasesInitialized) await initIAP(...)`. Si
  //   initIAP échouait (clé iOS placeholder, ou getCustomerInfo() down après
  //   configure), purchasesInitialized restait false MAIS le code continuait
  //   avec RC.logIn() / RC.purchasePackage() → erreur "no singleton instance"
  //   confuse pour l'user.
  // Maintenant : on attend le résultat de initIAP et on STOP si false.
  //   Message clair retourné à l'app pour afficher un Alert correct.
  if (!purchasesInitialized) {
    const initOk = await initIAP(user?.id);
    if (!initOk || !purchasesInitialized) {
      const platformKeyName = Platform.OS === 'ios' ? 'REVENUECAT_IOS_KEY' : 'REVENUECAT_ANDROID_KEY';
      console.warn(`[IAP] purchaseSubscription aborted: initIAP failed (${platformKeyName} probably missing/invalid)`);
      return {
        success: false,
        error: 'iap_not_configured',
        message: Platform.OS === 'ios'
          ? "Les achats in-app iOS ne sont pas encore configurés sur cette version. Veuillez réessayer après mise à jour."
          : "Les achats in-app ne sont pas configurés. Contactez le support.",
      };
    }
  }

  // ═══ FIX 2026-04 : forcer le logIn RC AVANT chaque achat ═══
  // Sans ça : si initIAP a été fait sans userId (au mount ProfileScreen),
  // RC reste en mode "anonymous" → l'achat est associé à un $RCAnonymousID
  // au lieu du userId Cognito. Conséquences :
  //   - dans le dashboard RC : multiples anonymous IDs sans status
  //   - achat impossible à restaurer entre devices
  //   - webhook RC ne peut pas matcher le user en DB
  // logIn() rattache l'anonymous ID actuel au userId Cognito → fusionne
  // les attributs et les achats. Idempotent : safe à appeler plusieurs fois.
  //
  // ═══ v1.7.53 : on push aussi l'email en attribut RC ═══
  // Avant : dashboard RC affichait juste l'ID Cognito, pas d'email visible.
  // Maintenant : setEmail() dès le logIn → email visible dans dashboard.
  if (user?.id) {
    try {
      const result = await RC.logIn(user.id);
      const created = result?.created;
      console.log(`[IAP] ✓ RC logIn user=${user.id.slice(0, 12)} (created=${created})`);

      // Set email + attributes (non-bloquant si échec)
      try {
        if (user.email && typeof RC.setEmail === 'function') {
          await RC.setEmail(user.email);
          console.log(`[IAP] ✓ RC setEmail: ${user.email}`);
        }
        if (user.name && typeof RC.setDisplayName === 'function') {
          await RC.setDisplayName(user.name);
        }
        if (typeof RC.setAttributes === 'function') {
          const attrs = {
            app_version: require('../../app.json').expo?.version || 'unknown',
            identified_at: new Date().toISOString(),
            country: user.country || 'FR',
          };
          await RC.setAttributes(attrs);
        }
      } catch (attrErr) {
        console.warn('[IAP] setEmail/setAttributes pre-purchase failed:', attrErr.message);
      }
    } catch (e) {
      console.warn('[IAP] logIn before purchase failed (non-bloquant):', e.message);
    }
  } else {
    console.warn('[IAP] ⚠️ tentative d\'achat sans user.id → restera anonymous');
  }

  const productId = PRODUCT_IDS[`${tier}_${period}`];
  if (!productId) throw new Error(`Unknown product: ${tier}_${period}`);

  try {
    // Récupérer l'Offering pour trouver le Package correspondant
    const offerings = await RC.getOfferings();
    const current = offerings?.current;
    const packages = current?.availablePackages || [];

    // ═══ LOGGING DIAGNOSTIC ═══
    // Liste toutes les offerings et tous les packages pour faciliter le debug
    // quand on ne trouve pas le produit.
    console.log('[IAP] getOfferings →',
      'current.identifier=', current?.identifier,
      'packages.count=', packages.length);
    console.log('[IAP] all offerings keys=', Object.keys(offerings?.all || {}));
    packages.forEach((p, i) => {
      console.log(`[IAP]   pkg[${i}] identifier=${p.identifier} productId=${p.product?.identifier || p.storeProduct?.identifier}`);
    });
    console.log(`[IAP] looking for productId=${productId} (tier=${tier} period=${period})`);

    // ═══ RECHERCHE MULTI-STRATÉGIE du package ═══
    // Robuste à toutes les variations de nommage que RC/Google peuvent retourner.
    // Essaie 6 stratégies, de la plus stricte à la plus permissive :
    //   1. Match exact sur productId brut (ex: "outfit_premium_monthly")
    //   2. Match avec suffixe Google Billing v5 (":monthly" / ":yearly")
    //   3. Match en splittant sur ":" (peu importe le suffixe)
    //   4. Match par package.identifier (ex: "premium_monthly", "$rc_monthly")
    //   5. Match par package.identifier RC standard ($rc_monthly, $rc_annual)
    //   6. Match heuristique : productId contient tier ET period

    let pkg = null;
    let matchStrategy = null;

    // Strat 1 : exact
    pkg = packages.find(p => {
      const pid = p.product?.identifier || p.storeProduct?.identifier || '';
      return pid === productId;
    });
    if (pkg) matchStrategy = 'exact';

    // Strat 2 : avec suffixe monthly/yearly
    if (!pkg) {
      pkg = packages.find(p => {
        const pid = p.product?.identifier || p.storeProduct?.identifier || '';
        return pid === `${productId}:${period}`;
      });
      if (pkg) matchStrategy = 'with-suffix';
    }

    // Strat 3 : split sur ":"
    if (!pkg) {
      pkg = packages.find(p => {
        const pid = p.product?.identifier || p.storeProduct?.identifier || '';
        return pid.split(':')[0] === productId;
      });
      if (pkg) matchStrategy = 'split-colon';
    }

    // Strat 4 : match par package.identifier (custom RC)
    if (!pkg) {
      const pkgKey = `${tier}_${period}`;
      pkg = packages.find(p => p.identifier === pkgKey);
      if (pkg) matchStrategy = 'package-identifier';
    }

    // Strat 5 : match par package.identifier RC standard
    if (!pkg) {
      const rcKey = period === 'yearly' ? '$rc_annual' : '$rc_monthly';
      pkg = packages.find(p => p.identifier === rcKey);
      if (pkg) matchStrategy = 'rc-standard-identifier';
    }

    // Strat 6 : heuristique (contient tier + period dans productId OU identifier)
    if (!pkg) {
      pkg = packages.find(p => {
        const pid = (p.product?.identifier || p.storeProduct?.identifier || '').toLowerCase();
        const id  = (p.identifier || '').toLowerCase();
        const hasT = pid.includes(tier) || id.includes(tier);
        const hasP = pid.includes(period) || id.includes(period);
        return hasT && hasP;
      });
      if (pkg) matchStrategy = 'heuristic';
    }

    console.log(`[IAP] package lookup strategy=${matchStrategy || 'FAILED'} for ${tier}/${period}`);

    if (!pkg) {
      const details = packages.map(p => ({
        id: p.identifier,
        prod: p.product?.identifier || p.storeProduct?.identifier,
      }));
      throw new Error(
        `Package ${productId} introuvable dans l'Offering "${current?.identifier || 'none'}". ` +
        `Packages disponibles: ${JSON.stringify(details)}`
      );
    }

    // Lancer l'achat via RevenueCat (qui gère le paiement Play Store)
    const { customerInfo, productIdentifier } = await RC.purchasePackage(pkg);

    // Vérifier que l'Entitlement attendu est bien actif
    // Convertir tier → identifier RC (basic → basic_access)
    const entitlementId = TIER_TO_ENTITLEMENT[tier] || tier;
    const entitlement = customerInfo?.entitlements?.active?.[entitlementId];
    if (!entitlement) {
      console.warn('[IAP] Achat OK mais Entitlement absent ?', Object.keys(customerInfo?.entitlements?.active || {}));
    }

    console.log(`[IAP] ✅ Achat ${productIdentifier} → tier=${tier} actif`);

    // Informer le backend (activation immédiate côté DB pendant que le webhook
    // RevenueCat va confirmer de son côté). Le webhook écrasera la valeur si
    // besoin (mais ça évite une latence où l'user voit encore "free").
    // ═══ FIX 2026-04 (v1.7.93) : envoyer la bonne plateforme + bons champs ═══
    // Avant : platform: 'google' hardcodé + googleToken/googleProductId même sur iOS
    //   → DB enregistrait à tort tous les achats iOS comme "google" → dashboard
    //     admin montrait des stats erronées.
    // Maintenant : on lit Platform.OS et on remplit appleTxnId/appleProductId pour
    //   iOS, googleToken/googleProductId pour Android.
    const isIOS = Platform.OS === 'ios';
    try {
      await activateSubscription({
        tier,
        period,
        platform: isIOS ? 'apple' : 'google',
        appleTxnId:      isIOS ? (entitlement?.originalPurchaseDate || entitlement?.latestPurchaseDate || null) : null,
        appleProductId:  isIOS ? productIdentifier : null,
        googleToken:     !isIOS ? (entitlement?.latestPurchaseDate || null) : null,
        googleProductId: !isIOS ? productIdentifier : null,
        revenueCatUserId: customerInfo?.originalAppUserId,
        billingEmail:    user?.email,
        billingName:     user?.name,
        billingCountry:  'FR',
      });
    } catch (e) {
      console.warn('[IAP] activateSubscription (optimiste) a échoué, le webhook RC prendra le relais:', e.message);
    }

    // v1.12.682 — GA4 : paiement abonnement IAP réussi
    // v1.12.876 — Enriched : value + currency + transaction_id pour GA4 standard
    try {
      const { trackEvent } = require('./AnalyticsService');
      const txId = customerInfo?.originalAppUserId || customerInfo?.latestExpirationDate || `${tier}_${Date.now()}`;
      // Tier price estimate (EUR)
      const TIER_PRICES = {
        basic_monthly: 5.99, basic_yearly: 59.90,
        premium_monthly: 9.99, premium_yearly: 99.90,
        gold_monthly: 16.99, gold_yearly: 169.90,
      };
      const value = TIER_PRICES[`${tier}_${period}`] || 0;
      trackEvent('purchase', {
        gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play',
        kind: 'subscription', tier, period, platform: Platform.OS,
        transaction_id: String(txId).slice(0, 100),
        value, currency: 'EUR',
        items: [{ item_id: `sub_${tier}_${period}`, item_name: `${tier} ${period}`, price: value, quantity: 1 }],
      });
    } catch {}
    // v1.12.945 — Notifier l'app qu'un achat / changement de formule a eu lieu.
    // AuthContext écoute cet event et rafraîchit le profil (tier réel) depuis le
    // backend avec retries → les limites (try-on, wardrobe, IA…) reflètent la
    // nouvelle formule sans attendre un redémarrage. Avant : on ne lisait que le
    // cache RevenueCat local (souvent en retard) → tier restait sur l'ancien.
    try { DeviceEventEmitter.emit('subscription_changed', { tier, period }); } catch {}
    return { success: true, tier, period, customerInfo };
  } catch (e) {
    // Annulation user = pas une erreur
    if (e.userCancelled) {
      return { success: false, cancelled: true };
    }
    console.warn('[IAP] purchaseSubscription error:', e.message);
    throw new Error(e.message || 'Erreur paiement');
  }
}

// ─── Acheter un add-on one-shot via RevenueCat (Phase B.3 bundle v3) ──
// addonId   : identifiant Mytenue (ex: 'addon_tryons_10', 'addon_scans_50')
// user      : objet { id, email, name }
// → lance RC.purchasePackage(). Le webhook RC (handler.js) insère ensuite
//   dans addon_purchases automatiquement (NON_RENEWING_PURCHASE).
//
// Prérequis RC dashboard :
//   1. Créer 8 products dans App Store Connect + Play Console (consumable
//      pour tryons/scans, non-consumable pour wardrobe).
//   2. Dans RevenueCat console : Products → ajouter les 8 productIds
//      iOS + Android.
//   3. Créer un Offering "addons" qui groupe les 8 packages avec les
//      identifiers : tryons_3, tryons_10, tryons_25, scans_50, scans_200,
//      wardrobe_50, wardrobe_100, wardrobe_250.
//      Ces identifiers DOIVENT matcher la colonne revenueCatIdentifier
//      de aws/lambda/addons-config.js (sinon le webhook ne reconnaît pas).
export async function purchaseAddon(addonId, user) {
  try {
    const { trackEvent } = require('./AnalyticsService');
    trackEvent('checkout_started', {
      addonId,
      gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play',
      kind: 'addon',
      platform: Platform.OS,
    });
  } catch {}

  const RC = await getPurchases();
  if (!RC) {
    throw new Error('RevenueCat SDK non disponible (Expo Go ne supporte pas les achats in-app).');
  }
  if (!purchasesInitialized) {
    const initOk = await initIAP(user?.id);
    if (!initOk || !purchasesInitialized) {
      throw new Error('RevenueCat non initialisé. Réessayez dans quelques secondes.');
    }
  }
  if (user?.id) {
    try { await RC.logIn(user.id); } catch (_) {}
  }

  // Mapping addonId Mytenue → revenueCatIdentifier (du package)
  // Doit rester aligné avec aws/lambda/addons-config.js
  // v1.12.952 — Aligné sur src/config/addonsConfig.js + aws/lambda/addons-config.js
  // (avant: addon_wardrobe_50/100/250 legacy → les add-ons réels n'étaient pas
  //  achetables en IAP, d'où le repli vers le web rejeté par Apple 3.1.1).
  const RC_IDENTIFIERS = {
    addon_tryons_3:            'tryons_3',
    addon_tryons_10:           'tryons_10',
    addon_tryons_25:           'tryons_25',
    addon_scans_50:            'scans_50',
    addon_scans_200:           'scans_200',
    addon_wardrobe_boost_50:   'wardrobe_boost_50',
    subscription_wardrobe_plus:'wardrobe_plus_monthly',
    subscription_wardrobe_max: 'wardrobe_max_monthly',
  };
  const rcId = RC_IDENTIFIERS[addonId];
  if (!rcId) throw new Error(`addonId inconnu: ${addonId}`);

  // Récupérer toutes les offerings et chercher le package
  const offerings = await RC.getOfferings();
  // Chercher d'abord dans l'offering "addons", sinon dans current, sinon partout
  const allPackages = [];
  for (const [_, offering] of Object.entries(offerings?.all || {})) {
    for (const pkg of (offering.availablePackages || [])) allPackages.push(pkg);
  }
  const pkg = allPackages.find(p => {
    const pid = p.product?.identifier || p.storeProduct?.identifier || p.identifier || '';
    return pid === rcId || pid.includes(rcId);
  });
  if (!pkg) {
    throw new Error(`Pack "${rcId}" introuvable. Vérifiez la config RevenueCat (Offering "addons").`);
  }

  try {
    const { customerInfo, productIdentifier } = await RC.purchasePackage(pkg);
    console.log(`[IAP] ✅ addon purchased: ${productIdentifier} (${addonId})`);
    // v1.12.682 — GA4 : paiement add-on IAP réussi
    // v1.12.876 — Enriched : value + currency + transaction_id
    try {
      const { trackEvent } = require('./AnalyticsService');
      const ADDON_PRICES = {
        addon_tryons_3: 1.99, addon_tryons_10: 4.99, addon_tryons_25: 9.99,
        addon_scans_50: 1.49, addon_scans_200: 3.99,
        addon_wardrobe_boost_50: 1.99,
        subscription_wardrobe_plus: 0.99, subscription_wardrobe_max: 2.49,
      };
      const value = ADDON_PRICES[addonId] || 0;
      trackEvent('purchase', {
        gateway: Platform.OS === 'ios' ? 'apple_iap' : 'google_play',
        kind: 'addon', addon_id: addonId, platform: Platform.OS,
        transaction_id: String(productIdentifier || `${addonId}_${Date.now()}`).slice(0, 100),
        value, currency: 'EUR',
        items: [{ item_id: addonId, item_name: addonId, price: value, quantity: 1 }],
      });
    } catch {}
    // v1.14.2 — FIX "scans achetés pas pris en compte / quota IA reste bloqué" :
    //   après l'achat d'un pack (scans/tryons), on émet `addon_balance_changed`
    //   → réinitialise le flag client `_aiQuotaExhausted` (guardAIQuota) et
    //   rafraîchit les compteurs. Plusieurs émissions échelonnées pour couvrir la
    //   latence du webhook RevenueCat qui crédite RÉELLEMENT l'add-on côté backend
    //   (sinon le 1er reset arrive avant le crédit → checkLimit revoit 0 scan).
    try {
      const { DeviceEventEmitter } = require('react-native');
      const fire = () => { try { DeviceEventEmitter.emit('addon_balance_changed', { addonId }); } catch {} };
      fire();
      setTimeout(fire, 3000);
      setTimeout(fire, 8000);
    } catch {}
    return { success: true, addonId, productIdentifier, customerInfo };
  } catch (e) {
    if (e.userCancelled) return { success: false, cancelled: true };
    console.warn('[IAP] purchaseAddon error:', e.message);
    throw new Error(e.message || 'Erreur paiement add-on');
  }
}

// ─── Restaurer les achats (obligatoire iOS, utile Android) ──
export async function restorePurchases(user) {
  const RC = await getPurchases();
  if (!RC) return { error: 'RevenueCat SDK non disponible (device ou build incomplete).' };
  if (!purchasesInitialized) await initIAP(user?.id);

  // ═══ FIX 2026-04 : logIn avant restore ═══
  // Sans ça, restorePurchases sur un anonymous ID ne ramène que les achats
  // de cet ID local — pas ceux faits depuis le compte Cognito sur d'autres
  // devices. logIn fusionne et restaure tout.
  if (user?.id) {
    try {
      await RC.logIn(user.id);
      console.log(`[IAP] ✓ RC logIn before restore user=${user.id.slice(0, 12)}`);
    } catch (e) {
      console.warn('[IAP] logIn before restore failed:', e.message);
    }
  }

  try {
    const customerInfo = await RC.restorePurchases();
    // Quel Entitlement est actif ?
    const active = customerInfo?.entitlements?.active || {};
    const allEntitlements = customerInfo?.entitlements?.all || {};
    const allPurchases = customerInfo?.allPurchasedProductIdentifiers || [];

    console.log('[IAP] restorePurchases — customerInfo:', {
      originalAppUserId: customerInfo?.originalAppUserId,
      activeEntitlements: Object.keys(active),
      allEntitlements: Object.keys(allEntitlements),
      allPurchases,
    });

    let activeTier = null;
    // Priorité au plus haut tier : on cherche l'entitlement RC puis on le
    // convertit en tier applicatif via ENTITLEMENT_TO_TIER.
    for (const entId of ['gold_access', 'premium_access', 'basic_access']) {
      if (active[entId]) {
        activeTier = ENTITLEMENT_TO_TIER[entId];
        break;
      }
    }
    if (!activeTier) {
      console.log('[IAP] restorePurchases: aucun entitlement actif');
      // Diagnostic détaillé pour le user
      return {
        success: false,
        reason: 'no_active_entitlement',
        rcUserId: customerInfo?.originalAppUserId,
        allPurchases,
        hasExpiredEntitlements: Object.keys(allEntitlements).length > 0,
      };
    }

    const entitlementId = TIER_TO_ENTITLEMENT[activeTier];
    const productId = active[entitlementId].productIdentifier;
    const parsed = parseProductId(productId);
    const period = parsed?.period || 'monthly';

    // Activer côté DB
    // v1.12.947 — platform correct selon l'OS (avant : 'google' en dur même sur
    // iOS → stats dashboard faussées + apple_product_id non renseigné).
    const isIOS = Platform.OS === 'ios';
    const result = await activateSubscription({
      tier:             activeTier,
      period,
      platform:         isIOS ? 'apple' : 'google',
      appleProductId:   isIOS ? productId : null,
      googleProductId:  isIOS ? null : productId,
      revenueCatUserId: customerInfo?.originalAppUserId,
      billingEmail:     user?.email,
    });
    // v1.12.992 — Le succès reflète l'ACTIVATION BACKEND (vérif RevenueCat serveur),
    // pas seulement l'entitlement RC côté client → évite le faux « Abonnement restauré »
    // quand le backend refuse (compte free / entitlement sandbox / non vérifié).
    const backendOk = result?.success === true;
    if (backendOk) {
      console.log(`[IAP] ✅ Restauré: tier=${activeTier} period=${period} platform=${isIOS ? 'apple' : 'google'}`);
      try { DeviceEventEmitter.emit('subscription_changed', { tier: activeTier, period }); } catch {}
      return { ...result, success: true, tier: activeTier, period };
    }
    console.log('[IAP] restore: activation backend refusée →', result?.error || result?.reason || 'unknown');
    return { success: false, reason: result?.reason || result?.error || 'not_activated' };
  } catch (e) {
    console.warn('[IAP] restorePurchases error:', e.message);
    return { success: false, error: e.message };
  }
}

// ─── Status actuel de l'abonnement (lecture uniquement) ──────
// Retourne le tier actif selon RevenueCat (source de vérité client-side)
export async function getActiveTier() {
  const RC = await getPurchases();
  if (!RC) return 'free';
  try {
    if (!purchasesInitialized) await initIAP();
    const info = await RC.getCustomerInfo();
    const active = info?.entitlements?.active || {};
    // Parcourir les entitlements RC par priorité décroissante (gold > premium > basic)
    for (const entId of ['gold_access', 'premium_access', 'basic_access']) {
      if (active[entId]) return ENTITLEMENT_TO_TIER[entId];
    }
    return 'free';
  } catch (e) {
    console.warn('[IAP] getActiveTier error:', e.message);
    return 'free';
  }
}

// ─── Fermer la connexion (lifecycle app) ────────────────────
// RevenueCat gère son cycle automatiquement, mais on expose pour compat
// avec l'ancien code qui appelait endIAP().
export async function endIAP() {
  // RevenueCat ne requiert pas de cleanup explicite.
  // Cette fonction existe pour rétrocompatibilité.
}

// ─── Compat : ancien code qui appelait purchaseSubscription(period) ──
// Les écrans qui passent juste (period) au lieu de (tier, period) vont
// recevoir tier='premium' par défaut pour ne pas casser.
export async function _legacyPurchase(period, user) {
  console.warn('[IAP] _legacyPurchase: appel sans tier — default premium');
  return purchaseSubscription('premium', period, user);
}
